import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  PoTemplatesDataCollectionDocument,
  type PoTemplateItemFragment,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { buildUrl } from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToRelativeTime } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { getPoTemplateVersionStatusInfo } from 'modules/procurement/pages/PoTemplatesPage/utils'

const PoTemplatesContent = () => {
  const { navigation } = useNavigation()

  const source = useGraphqlDataCollectionSource({
    document: PoTemplatesDataCollectionDocument,
    mapData: (data) => data.procurement.poTemplatesConnection.edges.map((edge) => edge.node) ?? [],
    mapPagination: (data) => ({
      total: data.procurement.poTemplatesConnection.totalCount ?? 0,
    }),
    mapVariables: ({ sortings }) => ({
      sortOrder: sortings?.[0],
    }),
    itemUrl: (item: PoTemplateItemFragment) =>
      navigation.settings.procurement.poTemplate.id.fullPath({ id: item.id }),
  })

  return (
    <OneDataCollection
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('procurement.purchase_requests.settings.po_templates.columns.name'),
                render: (item: PoTemplateItemFragment) => ({
                  type: 'text' as const,
                  value: { text: item.name },
                }),
              },
              {
                label: i18n.t('procurement.purchase_requests.settings.po_templates.columns.status'),
                render: (item: PoTemplateItemFragment) => {
                  const activeVersion = item.poTemplateVersionsConnection.edges[0]?.node
                  const statusInfo = activeVersion
                    ? getPoTemplateVersionStatusInfo(activeVersion.status)
                    : undefined
                  return {
                    type: 'status' as const,
                    value: {
                      label: statusInfo?.label ?? '-',
                      status: statusInfo?.status ?? 'neutral',
                    },
                  }
                },
              },
              {
                label: i18n.t(
                  'procurement.purchase_requests.settings.po_templates.columns.created_by'
                ),
                render: (item: PoTemplateItemFragment) => {
                  if (!item.createdBy) {
                    return { type: 'text' as const, value: { text: '-' } }
                  }

                  return {
                    type: 'person' as const,
                    value: {
                      firstName: item.createdBy.firstName,
                      lastName: item.createdBy.lastName,
                      src: buildUrl(item.createdBy.avatar?.url ?? ''),
                    },
                  }
                },
              },
              {
                label: i18n.t(
                  'procurement.purchase_requests.settings.po_templates.columns.last_modified'
                ),
                render: (item: PoTemplateItemFragment) => {
                  const publishedAt = item.poTemplateVersionsConnection.edges[0]?.node?.publishedAt
                  return {
                    type: 'text' as const,
                    value: {
                      text: publishedAt ? dateBackendDateTimeToRelativeTime(publishedAt) : '-',
                    },
                  }
                },
              },
            ],
            frozenColumns: 1 as const,
          },
        },
      ]}
      emptyStates={{
        'no-data': {
          title: i18n.t('procurement.purchase_requests.settings.po_templates.empty_state_title'),
          description: i18n.t(
            'procurement.purchase_requests.settings.po_templates.empty_state_description'
          ),
        },
      }}
    />
  )
}

export default PoTemplatesContent
